/**
 * MicroEAI Explainability — Enterprise (Python-aligned)
 * Config, XRS risk scoring, developer guidance, and data derivation from legacy explainability data.
 */

export const EXPLAINABILITY_CONFIG = {
  dominance_share: 0.35,
  faithfulness_ref: 0.4,
  xrs_low: 0.3,
  xrs_high: 0.6,
  lime_stable_mult: 0.75,
  lime_moderate_mult: 1.5,
  guidance_instability_warn: 0.4,
  guidance_faithfulness_warn: 0.4,
  lime_std_scale: 5.0,
  risk_weights: {
    concentration: 0.3,
    instability: 0.3,
    faithfulness: 0.3,
    sensitive: 0.1
  },
  top_k: 5
} as const;

export type SourceAttribution = Record<string, number>;
export type TokenCategory = 'Sensitive' | 'Whitelisted' | 'Neutral';

export interface ShapTopKItem {
  token: string;
  relativeInfluence: number;
  category: TokenCategory;
}

export interface LimeStabilityRow {
  token: string;
  status: 'Stable' | 'Moderate' | 'Unstable';
  std: number;
}

export interface ExplainabilityDerived {
  sourceAttribution: SourceAttribution;
  shapTopK: ShapTopKItem[];
  limeStability: LimeStabilityRow[];
  meanAopc: number;
  medianLimeStd: number;
  xrs: number;
  verdict: 'LOW' | 'MODERATE' | 'HIGH';
  drivers: Record<string, number>;
  developerGuidance: string[];
}

const C = EXPLAINABILITY_CONFIG;

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

export function microeaiXrs(
  top1Share: number,
  medianLimeStd: number,
  meanAopc: number,
  sensitiveInTopk: boolean
): { xrs: number; verdict: 'LOW' | 'MODERATE' | 'HIGH'; drivers: Record<string, number> } {
  const domThr = C.dominance_share;
  const aopcRef = C.faithfulness_ref;
  const w = C.risk_weights;

  const concentrationRisk = clamp((top1Share - domThr) / Math.max(1e-9, 1 - domThr), 0, 1);
  const instabilityRisk = Math.tanh(medianLimeStd * C.lime_std_scale);
  const faithfulnessRisk = clamp((aopcRef - meanAopc) / Math.max(1e-9, aopcRef), 0, 1);
  const sensitiveRisk = sensitiveInTopk ? 1 : 0;

  const xrs = clamp(
    w.concentration * concentrationRisk +
      w.instability * instabilityRisk +
      w.faithfulness * faithfulnessRisk +
      w.sensitive * sensitiveRisk,
    0,
    1
  );

  let verdict: 'LOW' | 'MODERATE' | 'HIGH' = 'LOW';
  if (xrs > C.xrs_high) verdict = 'HIGH';
  else if (xrs > C.xrs_low) verdict = 'MODERATE';

  const drivers: Record<string, number> = {
    top1_share: top1Share,
    concentration_risk: concentrationRisk,
    median_lime_std: medianLimeStd,
    instability_risk: instabilityRisk,
    mean_aopc: meanAopc,
    faithfulness_risk: faithfulnessRisk,
    sensitive_in_topk: sensitiveRisk,
    xrs
  };
  return { xrs, verdict, drivers };
}

export function developerGuidance(drivers: Record<string, number>): string[] {
  const out: string[] = [];
  if (drivers.concentration_risk > 0) {
    out.push('Reduce over-reliance on a dominant token (add context, restructure prompts/outputs).');
  } else {
    out.push('No excessive token dominance detected.');
  }
  if (drivers.instability_risk > C.guidance_instability_warn) {
    out.push('Explanation stability is weak/moderate. Add constraints or make reasoning structure more consistent.');
  } else {
    out.push('Explanation stability appears acceptable.');
  }
  if (drivers.faithfulness_risk > C.guidance_faithfulness_warn) {
    out.push('Faithfulness is low. Consider making reasoning more explicit (clearer key terms, less diffuse outputs).');
  } else {
    out.push('Faithfulness is acceptable for descriptive analysis.');
  }
  if (drivers.sensitive_in_topk > 0) {
    out.push(
      'Sensitive term appears among top influences. Verify necessity and ensure governance/whitelisting is correct.'
    );
  } else {
    out.push('No sensitive dominance detected in Top tokens.');
  }
  return out;
}

const SENSITIVE_TOKENS = new Set(
  [
    'young',
    'child',
    'teen',
    'old',
    'elderly',
    'senior',
    'male',
    'man',
    'he',
    'female',
    'woman',
    'she',
    'cisgender',
    'transgender',
    'white',
    'caucasian',
    'black',
    'asian',
    'hispanic',
    'european',
    'latino',
    'african',
    'pregnant',
    'healthy',
    'cancer',
    'abled',
    'disabled',
    'stable',
    'depression',
    'anxiety',
    'wealthy',
    'poor',
    'urban',
    'rural',
    'christian',
    'hindu',
    'muslim',
    'atheist',
    'jewish'
  ].map(s => s.toLowerCase())
);
const WHITELIST_TOKENS = new Set(
  [
    'pregnant',
    'pregnancy',
    'embryo',
    'fertility',
    'mutation',
    'genome',
    'disease',
    'cancer',
    'male',
    'female',
    'he',
    'she',
    'man',
    'woman',
    'abled',
    'disabled',
    'child',
    'elderly',
    'stable',
    'anatomy',
    'biomechanical',
    'spinal',
    'disc',
    'foraminal',
    'height',
    'width',
    'area',
    'delta',
    'load'
  ].map(s => s.toLowerCase())
);

function classifyToken(token: string): TokenCategory {
  const t = token.toLowerCase();
  if (WHITELIST_TOKENS.has(t)) return 'Whitelisted';
  if (SENSITIVE_TOKENS.has(t)) return 'Sensitive';
  return 'Neutral';
}

export interface LegacyExplainabilityEntry {
  Feature?: string;
  Importance?: number;
  [key: string]: string | number | undefined;
}

/**
 * Derive enterprise explainability payload from legacy ML/LLM feature importance entries.
 * Builds source attribution (synthetic 1/3 each or Output-heavy), SHAP top-k, LIME stability placeholder, faithfulness placeholder.
 */
export function deriveExplainabilityFromLegacy(
  mlEntries: LegacyExplainabilityEntry[],
  llmEntries: LegacyExplainabilityEntry[]
): ExplainabilityDerived {
  const topK = C.top_k;
  const combined: { feature: string; importance: number }[] = [];
  const push = (entry: LegacyExplainabilityEntry) => {
    const f = String(entry.Feature ?? entry.feature ?? entry.Token ?? entry.token ?? '').trim();
    const imp = Number(entry.Importance ?? entry.importance ?? entry.weight ?? 0);
    if (f && Number.isFinite(imp)) combined.push({ feature: f, importance: Math.abs(imp) });
  };
  mlEntries.forEach(push);
  llmEntries.forEach(push);

  /** Prefer human-readable tokens (e.g. "Credit_History", "stable") over generic LIME names ("feature_8", "feature_2") for Plot 2. */
  const isGenericFeatureName = (f: string): boolean =>
    /^feature\s*_\s*\d+/i.test(f) || /^feature\s+\d+/i.test(f) || /\bfeature\s*_\s*\d+\s*[<>=]/.test(f);

  combined.sort((a, b) => {
    const aGeneric = isGenericFeatureName(a.feature);
    const bGeneric = isGenericFeatureName(b.feature);
    if (aGeneric !== bGeneric) return (aGeneric ? 1 : 0) - (bGeneric ? 1 : 0);
    return b.importance - a.importance;
  });
  const top = combined.slice(0, topK);
  const total = top.reduce((s, x) => s + x.importance, 0) || 1;
  const shapTopK: ShapTopKItem[] = top.map(({ feature, importance }) => ({
    token: feature,
    relativeInfluence: importance / total,
    category: classifyToken(feature)
  }));

  const top1Share = shapTopK.length > 0 ? shapTopK[0].relativeInfluence : 0;
  const sensitiveInTopk = shapTopK.some(x => x.category === 'Sensitive');

  const medianLimeStd = 0.15;
  const meanAopc = 0.45;
  const { xrs, verdict, drivers } = microeaiXrs(top1Share, medianLimeStd, meanAopc, sensitiveInTopk);

  const stableMult = C.lime_stable_mult;
  const moderateMult = C.lime_moderate_mult;
  const limeStability: LimeStabilityRow[] = shapTopK.map((item, i) => {
    const std = 0.05 + i * 0.03;
    let status: 'Stable' | 'Moderate' | 'Unstable' = 'Stable';
    if (std > moderateMult * medianLimeStd) status = 'Unstable';
    else if (std > stableMult * medianLimeStd) status = 'Moderate';
    return { token: item.token, status, std };
  });

  const sourceAttribution: SourceAttribution = {
    Prompt: 0.25,
    Metadata: 0.25,
    Output: 0.5
  };

  return {
    sourceAttribution,
    shapTopK,
    limeStability,
    meanAopc,
    medianLimeStd,
    xrs,
    verdict,
    drivers,
    developerGuidance: developerGuidance(drivers)
  };
}
